import { useI18n, type MessageKey } from '@platform/i18n';
import { ActionOverflowMenu, defineActions, resolveActions, type ActionDef } from '@platform/ui';
import { CalendarRangeIcon, LockIcon, PaperclipIcon, RepeatIcon, Trash2Icon, VideoIcon } from 'lucide-react';

/** Local editor disclosures only; no native item or permission contract is created here. */
export interface EventSecondaryMenuProps {
  disabled: boolean;
  canRepeat: boolean;
  repeatVisible: boolean;
  privateEvent: boolean;
  onlineVisible: boolean;
  schedulingVisible: boolean;
  onToggleRepeat: () => void;
  onTogglePrivate: () => void;
  onToggleOnline: () => void;
  onAttach: () => void;
  onToggleScheduling: () => void;
  /** Supplied only when the existing editor permits deletion; preserves its Undo action. */
  deleteLabel?: MessageKey;
  onDelete?: () => void;
}
type Callback = 'onToggleRepeat' | 'onTogglePrivate' | 'onToggleOnline' | 'onAttach' | 'onToggleScheduling' | 'onDelete';
const enabled: ActionDef<EventSecondaryMenuProps>['enabled'] = ({ targets }) => !targets[0]?.disabled;
const run = (callback: Callback): ActionDef<EventSecondaryMenuProps>['run'] => ({ targets }) => {
  const editor = targets[0];
  if (editor && !editor.disabled) editor[callback]?.();
};
const registry = defineActions<EventSecondaryMenuProps>([
  { id: 'event.repeatDisclosure', label: 'webmail.calendarUx.editor.showRepeat',
    labelFor: ({ targets }) => targets[0]?.repeatVisible ? 'webmail.calendarUx.editor.hideRepeat' : 'webmail.calendarUx.editor.showRepeat',
    icon: RepeatIcon, group: 'options', surfaces: ['overflow'], visible: ({ targets }) => Boolean(targets[0]?.canRepeat), enabled, run: run('onToggleRepeat') },
  { id: 'event.privateToggle', label: 'webmail.calendarUx.editor.markPrivate',
    labelFor: ({ targets }) => targets[0]?.privateEvent ? 'webmail.calendarUx.editor.makePublic' : 'webmail.calendarUx.editor.markPrivate',
    icon: LockIcon, group: 'options', surfaces: ['overflow'], enabled, run: run('onTogglePrivate') },
  { id: 'event.onlineDisclosure', label: 'webmail.calendarUx.editor.showOnline',
    labelFor: ({ targets }) => targets[0]?.onlineVisible ? 'webmail.calendarUx.editor.hideOnline' : 'webmail.calendarUx.editor.showOnline',
    icon: VideoIcon, group: 'options', surfaces: ['overflow'], enabled, run: run('onToggleOnline') },
  { id: 'event.attach', label: 'webmail.calendarUx.editor.attach', icon: PaperclipIcon,
    group: 'options', surfaces: ['overflow'], enabled, run: run('onAttach') },
  { id: 'event.schedulingDisclosure', label: 'webmail.calendarUx.editor.showScheduling',
    labelFor: ({ targets }) => targets[0]?.schedulingVisible ? 'webmail.calendarUx.editor.hideScheduling' : 'webmail.calendarUx.editor.showScheduling',
    icon: CalendarRangeIcon, group: 'options', surfaces: ['overflow'], enabled, run: run('onToggleScheduling') },
  { id: 'event.remove', label: 'webmail.calendar.delete', labelFor: ({ targets }) => targets[0]?.deleteLabel ?? 'webmail.calendar.delete',
    icon: Trash2Icon, group: 'remove', surfaces: ['overflow'], danger: true,
    visible: ({ targets }) => Boolean(targets[0]?.onDelete && targets[0]?.deleteLabel), enabled, run: run('onDelete') },
]);

export const eventSecondaryActions = (props: EventSecondaryMenuProps) => resolveActions(registry, [props], 'overflow');

export function EventSecondaryMenu(props: EventSecondaryMenuProps) {
  const { t } = useI18n();
  return <ActionOverflowMenu actions={eventSecondaryActions(props)} size="icon-sm"
    label={t('webmail.calendarUx.editor.moreActions')} title={t('webmail.calendarUx.editor.optionsTitle')} />;
}
